/**
 * What people and their agents said about a bug, in order, with the date.
 *
 * It exists for one moment above all: a tester sends a bug back and writes why. The developer — and
 * the agent that picks the work up again — reads that here, next to the bug, rather than guessing
 * from a status that moved. A returned comment is marked, because it is the one that changed
 * somebody's day.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, MessageSquare, Send, Undo2 } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n, type Locale } from '../lib/i18n'
import { formatDateTime } from '../lib/format'
import { Skeleton } from '../components/primitives'
import type { BugComment } from '../lib/types'

export function BugComments({ taskId, canWrite, count }: { taskId: string; canWrite: boolean; count: number }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const { data: comments, isLoading } = useQuery({
    queryKey: ['bug-comments', taskId],
    queryFn: () => api.bugHunt.comments(taskId),
    enabled: open,
    refetchInterval: open ? 20_000 : false,
  })

  const say = useMutation({
    mutationFn: (value: string) => api.bugHunt.comment(taskId, value),
    onSuccess: () => {
      setText('')
      void queryClient.invalidateQueries({ queryKey: ['bug-comments', taskId] })
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
    },
  })

  return (
    <details className="bh-comments" open={open} onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}>
      <summary className="bh-comments__summary small">
        <MessageSquare size={13} aria-hidden /> {t('bugHunt.comments', { count })}
      </summary>

      <div className="stack stack--tight bh-comments__body">
        {isLoading && <Skeleton height={40} />}
        {comments?.length === 0 && <span className="tiny muted">{t('bugHunt.noComments')}</span>}

        {comments?.map((comment) => (
          <Comment key={comment.id} comment={comment} locale={locale} />
        ))}

        {canWrite && (
          <form
            className="row row--tight"
            onSubmit={(event) => {
              event.preventDefault()
              const value = text.trim()
              if (value && !say.isPending) say.mutate(value)
            }}
          >
            <input
              className="input"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t('bugHunt.commentPlaceholder')}
              maxLength={4000}
              aria-label={t('bugHunt.commentPlaceholder')}
            />
            <button type="submit" className="btn btn--sm btn--primary btn--icon" disabled={!text.trim() || say.isPending} aria-label={t('room.say.send')}>
              <Send size={13} />
            </button>
          </form>
        )}
      </div>
    </details>
  )
}

function Comment({ comment, locale }: { comment: BugComment; locale: Locale }) {
  const { t } = useI18n()
  const returned = comment.kind === 'Returned'

  return (
    <div className={`bh-comment${returned ? ' bh-comment--returned' : ''}`}>
      <div className="row row--tight tiny muted">
        {returned && <Undo2 size={11} aria-hidden />}
        {comment.byAgent && <Bot size={11} aria-hidden />}
        <span className="bold">{comment.authorName}</span>
        <span>{formatDateTime(comment.createdAt, locale)}</span>
        {returned && <span className="bh-comment__mark">{t('bugHunt.returnedToDev')}</span>}
        {comment.kind === 'Testing' && <span className="bh-comment__mark">{t('bugState.InTesting')}</span>}
      </div>
      <p className="bh-comment__text">{comment.text}</p>
    </div>
  )
}
